import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useState } from 'react';
import { Image, Linking, Pressable, View } from 'react-native';

import { ActivityIndicator, Text, useTheme } from '@/components/ui';
import type { MessageAttachment } from '@/services/data-service';
import {
	fetchDocumentObjectUrl,
	formatDocumentSize,
	isImage,
	openDocumentInNewTab,
	downloadDocument,
} from '@/utils/document-download';

/**
 * What the homeowner actually sent (#369).
 *
 * Photos of the fence, a PDF quote, the signed form — for an improvement request these ARE
 * the request, and a filename with no way to look at it makes staff go back to Outlook.
 *
 * Everything routes through `/attachments/:id/content`, never a storage URL: the endpoint
 * applies the contractor gate and keeps the storage layout out of the client. That URL
 * needs our auth header, which `<img src>` cannot send — hence the fetch-to-blob dance in
 * `document-download.ts`.
 */

export const contentPath = (id: string) => `/attachments/${id}/content`;

/**
 * Open a file the way its type deserves.
 *
 * PDFs get a real TAB, never an in-page frame — Chrome refuses to run its PDF viewer
 * inside a sandbox, and a tab also brings find, paging and print. Anything the browser
 * cannot render is downloaded rather than opening a tab that immediately offers a
 * download anyway.
 */
export async function openAttachment(attachment: MessageAttachment): Promise<void> {
	const type = attachment.fileType;
	if (type === 'pdf' || isImage(type) || type === 'txt') {
		const opened = await openDocumentInNewTab(contentPath(attachment.id), attachment.fileName);
		if (opened) return;
	}
	await downloadDocument(contentPath(attachment.id), attachment.fileName);
}

/**
 * Save the file, always (#465).
 *
 * Downloading used to be reachable only as a FALLBACK — `openAttachment` above downloads
 * when a type will not preview, or when the popup blocker eats the tab. So for the common
 * cases (a photo, a PDF quote) there was no way to get the file onto disk at all: it
 * opened in a tab and that was that. Staff who need to attach it to an email, hand it to a
 * vendor, or file it were stuck.
 */
export async function downloadAttachment(attachment: MessageAttachment): Promise<void> {
	await downloadDocument(contentPath(attachment.id), attachment.fileName);
}

/** The save-to-disk affordance, identical on a tile and on a row. */
const DownloadButton: React.FC<{ attachment: MessageAttachment; onDownload: () => void }> = ({
	attachment, onDownload,
}) => {
	const theme = useTheme();
	return (
		<Pressable
			onPress={onDownload}
			testID={`btn-download-attachment-${attachment.id}`}
			// Real padding rather than hitSlop — RN-Web's Pressable drops hitSlop.
			style={{ paddingVertical: 6, paddingHorizontal: 8 }}
		>
			<Ionicons name="download-outline" size={16} color={theme.colors.primary} />
		</Pressable>
	);
};

/** Inline preview. Images only; anything else is a row with an icon. */
export const AttachmentThumbnail: React.FC<{
	attachment: MessageAttachment;
	onOpen: () => void;
	/**
	 * Full-width preview instead of a contact-sheet tile.
	 *
	 * On a request, the photo IS the submission — a board decides on what the fence looks
	 * like, and a 132px tile of a 3072px phone photo shows nothing. The inbox keeps the
	 * tiles: there the photo is an enclosure on a message you are triaging, not the thing
	 * you are studying.
	 */
	fullWidth?: boolean;
	/** Absent hides the save button — the request report renders these for print, not for use. */
	onDownload?: () => void;
}> = ({ attachment, onOpen, fullWidth = false, onDownload }) => {
	const theme = useTheme();
	const [url, setUrl] = useState<string | null>(null);
	const [failed, setFailed] = useState(false);

	useEffect(() => {
		let objectUrl: string | null = null;
		let cancelled = false;
		(async () => {
			try {
				const next = await fetchDocumentObjectUrl(contentPath(attachment.id));
				if (cancelled) { URL.revokeObjectURL(next); return; }
				objectUrl = next;
				setUrl(next);
			} catch {
				if (!cancelled) setFailed(true);
			}
		})();
		// Revoke on unmount or the blob outlives the screen — a handful of phone photos is
		// tens of MB held for the life of the tab.
		return () => {
			cancelled = true;
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [attachment.id]);

	return (
		/*
			The save button is a SIBLING of the preview target, never nested inside it. On
			RN-Web a Pressable is a div with an onClick and clicks bubble, so a nested button
			fires its own handler AND the tile's — pressing Save on a PDF would write the file
			and open a preview tab. (It is also invalid HTML: buttons cannot contain buttons.)
		*/
		<View
			style={{
				width: fullWidth ? '100%' : 132,
				marginRight: fullWidth ? 0 : 8,
				marginBottom: fullWidth ? 12 : 8,
				borderRadius: 8, overflow: 'hidden',
				borderWidth: 1, borderColor: theme.colors.outlineVariant ?? theme.colors.outline,
			}}
		>
			<Pressable onPress={onOpen} testID={`attachment-thumb-${attachment.id}`}>
				<View
					style={{
						height: fullWidth ? 460 : 100,
						alignItems: 'center', justifyContent: 'center',
						backgroundColor: theme.colors.surfaceVariant,
					}}
				>
					{url ? (
						// `contain` at full width: a cropped tile is fine on a contact sheet, but
						// cropping the top off a roofline is exactly what a reviewer needs to see.
						<Image
							source={{ uri: url }}
							style={{ width: '100%', height: '100%' }}
							resizeMode={fullWidth ? 'contain' : 'cover'}
						/>
					) : failed ? (
						<Ionicons name="alert-circle-outline" size={22} color={theme.colors.error} />
					) : (
						<ActivityIndicator size="small" />
					)}
				</View>
			</Pressable>
			<View style={{ flexDirection: 'row', alignItems: 'center' }}>
				{/* The form's own label beats a CDN UUID filename every time. */}
				<Text numberOfLines={1} style={{ flex: 1, minWidth: 0, fontSize: fullWidth ? 12 : 11, padding: fullWidth ? 8 : 6 }}>
					{attachment.label || attachment.fileName}
				</Text>
				{onDownload ? <DownloadButton attachment={attachment} onDownload={onDownload} /> : null}
			</View>
		</View>
	);
};

export const AttachmentRow: React.FC<{
	attachment: MessageAttachment;
	onOpen: () => void;
	onDownload?: () => void;
}> = ({ attachment, onOpen, onDownload }) => {
	const theme = useTheme();
	const icon = attachment.fileType === 'pdf' ? 'document-text-outline' : 'document-attach-outline';
	return (
		// Save sits BESIDE the row, not inside it — see the thumbnail above for why.
		<View
			style={{
				flexDirection: 'row', alignItems: 'center',
				paddingVertical: 10, paddingHorizontal: 12, marginBottom: 6,
				borderRadius: 8, borderWidth: 1,
				borderColor: theme.colors.outlineVariant ?? theme.colors.outline,
			}}
		>
			<Pressable
				onPress={onOpen}
				testID={`attachment-row-${attachment.id}`}
				style={{ flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'center' }}
			>
				<Ionicons name={icon} size={18} color={theme.colors.primary} style={{ marginRight: 10 }} />
				<View style={{ flex: 1, minWidth: 0 }}>
					<Text numberOfLines={1} style={{ fontWeight: '600' }}>{attachment.label || attachment.fileName}</Text>
					{attachment.label ? (
						<Text numberOfLines={1} style={{ fontSize: 11, color: theme.colors.onSurfaceVariant }}>{attachment.fileName}</Text>
					) : null}
				</View>
				{attachment.fileSize ? (
					<Text style={{ fontSize: 11, color: theme.colors.onSurfaceVariant, marginLeft: 8 }}>
						{formatDocumentSize(attachment.fileSize)}
					</Text>
				) : null}
			</Pressable>
			{onDownload ? <DownloadButton attachment={attachment} onDownload={onDownload} /> : null}
		</View>
	);
};

export interface MessageAttachmentsProps {
	attachments: MessageAttachment[];
	/** Upload links in the body we do not hold yet. */
	pendingUploadLinks: string[];
	canFetch: boolean;
	fetching: boolean;
	onFetchLinks: () => void;
	onError: (error: unknown, fallback: string) => void;
}

export const MessageAttachments: React.FC<MessageAttachmentsProps> = ({
	attachments, pendingUploadLinks, canFetch, fetching, onFetchLinks, onError,
}) => {
	const theme = useTheme();
	if (attachments.length === 0 && pendingUploadLinks.length === 0) return null;

	const open = async (attachment: MessageAttachment) => {
		try {
			await openAttachment(attachment);
		} catch (error) {
			onError(error, 'Could not open that file.');
		}
	};

	const save = async (attachment: MessageAttachment) => {
		try {
			await downloadAttachment(attachment);
		} catch (error) {
			onError(error, 'Could not download that file.');
		}
	};

	const images = attachments.filter((a) => isImage(a.fileType));
	const files = attachments.filter((a) => !isImage(a.fileType));

	return (
		<View style={{ marginTop: 14 }} testID="message-attachments">
			<Text style={{ fontSize: 12, fontWeight: '700', letterSpacing: 0.4, color: theme.colors.onSurfaceVariant, marginBottom: 8 }}>
				{attachments.length > 0 ? `ATTACHMENTS (${attachments.length})` : 'ATTACHMENTS'}
			</Text>

			{images.length > 0 ? (
				<View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
					{images.map((a) => (
						<AttachmentThumbnail
							key={a.id}
							attachment={a}
							onOpen={() => void open(a)}
							onDownload={() => void save(a)}
						/>
					))}
				</View>
			) : null}

			{files.map((a) => (
				<AttachmentRow key={a.id} attachment={a} onOpen={() => void open(a)} onDownload={() => void save(a)} />
			))}

			{/*
				Links we have NOT pulled in. Shown rather than hidden because the ingest-time
				download is best-effort: a CDN blip used to lose the homeowner's photos with
				nothing left pointing at the gap. The link stays clickable either way, so the
				file is reachable even when the fetch keeps failing.
			*/}
			{pendingUploadLinks.length > 0 ? (
				<View
					style={{
						marginTop: 4, padding: 10, borderRadius: 8,
						borderWidth: 1, borderStyle: 'dashed',
						borderColor: theme.colors.outline,
					}}
					testID="pending-upload-links"
				>
					<Text style={{ color: theme.colors.onSurfaceVariant, marginBottom: 6 }}>
						{pendingUploadLinks.length === 1
							? 'This message links a file we have not saved yet.'
							: `This message links ${pendingUploadLinks.length} files we have not saved yet.`}
					</Text>
					{pendingUploadLinks.map((url) => (
						<Pressable key={url} onPress={() => { void Linking.openURL(url); }} style={{ paddingVertical: 3 }}>
							<Text numberOfLines={1} style={{ color: theme.colors.primary, textDecorationLine: 'underline', fontSize: 12 }}>
								{decodeURIComponent(url.split('/').pop() || url)}
							</Text>
						</Pressable>
					))}
					{canFetch ? (
						<Pressable
							onPress={onFetchLinks}
							disabled={fetching}
							testID="btn-fetch-upload-links"
							style={{
								flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start',
								marginTop: 8, paddingVertical: 8, paddingHorizontal: 12,
								borderRadius: 8, backgroundColor: theme.colors.primary, opacity: fetching ? 0.6 : 1,
							}}
						>
							{fetching
								? <ActivityIndicator size="small" color={theme.colors.onPrimary} />
								: <Ionicons name="cloud-download-outline" size={15} color={theme.colors.onPrimary} style={{ marginRight: 6 }} />}
							<Text style={{ fontWeight: '700', color: theme.colors.onPrimary, marginLeft: fetching ? 6 : 0 }}>
								{fetching ? 'Saving…' : 'Save files'}
							</Text>
						</Pressable>
					) : null}
				</View>
			) : null}
		</View>
	);
};

export default MessageAttachments;
